import React, { Component } from 'react'
import { css, cx } from '@emotion/css'
import { Row, Box, FormValidator } from '../../legacy-design-system/form'
import {
  MultiSelectCombobox,
  SingleSelectComboboxString
} from '../../legacy-design-system/combobox'
import { Checkbox } from '../../legacy-design-system/checkbox'
import { ProgressThermometer } from '../../legacy-design-system/thermometer'
import { I18nConsumer } from '../i18n-provider'
import {
  CustomMetadataForm,
  FormField,
  ReactionOperation
} from './custom-metadata-form'
import { FormattedMessage } from 'react-intl'
import {
  formSectionTitleCss,
  metadataCss,
  readOnlyGridCss
} from '../../legacy-design-system/text'
import { ChildFieldType, ParentFieldType } from './custom-metadata-field-input'
import { logError, logSuccess } from '../toaster'
import { ConfirmationDialog } from '../../legacy-design-system/modal'
import { Label } from '../../legacy-design-system/form-label'
import { updateRoute } from '../route'
import { HelpText } from '../server-settings/form-components/help-text'
import { WithTooltip } from '../../design-system'
import { CsHelpCircledIcon } from '../../legacy-design-system/icons/help-circled-icon'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { formatStyles } from '../react-intl-helpers'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import {
  SingleSelect,
  NumberInput,
  Radio
} from '@contrast-security-inc/design-system-inputs'
import { SplitContext } from '../../ng/splitio-wrapper'
import { allServerEnvironments, serverEnvI18n } from '../server-environment'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { useCurrentUser } from 'contrastBase/state'

type Importance = 'UNIMPORTANT' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
const importanceTo18N: Record<Importance, string> = {
  UNIMPORTANT: 'title_appImportanceLevelUnimportant',
  LOW: 'title_appImportanceLevelLow',
  MEDIUM: 'title_appImportanceLevelMedium',
  HIGH: 'title_appImportanceLevelHigh',
  CRITICAL: 'title_appImportanceLevelCritical'
}

interface Policy {
  readonly id: string
  readonly name: string
}

type PolicyType = 'remediation' | 'compliance'
export type PolicyAction = 'AUTO_REMEDIATE' | 'NOTIFY'

interface TypedPolicy {
  readonly id: string
  readonly name: string
  readonly type: PolicyType
}

export interface Field {
  readonly id: string
  readonly type: ParentFieldType
  readonly displayLabel: string
  readonly agentLabel: string
  readonly required: boolean
  readonly unique: boolean
  readonly subfields?: Subfield[]
  readonly isTypeEditable?: boolean
}

export interface Subfield {
  readonly id: string
  readonly type: ChildFieldType
  readonly displayLabel: string
  readonly agentLabel: string
  readonly required: boolean
  readonly unique: boolean
}

export interface SubmitField {
  readonly id?: string
  readonly type: ParentFieldType
  readonly displayLabel: string
  readonly agentLabel: string
  readonly required: boolean
  readonly unique: boolean
  readonly subfields?: SubmitSubfield[]
}

export interface SubmitSubfield {
  readonly id?: string
  readonly type: ChildFieldType
  readonly displayLabel: string
  readonly agentLabel: string
  readonly required: boolean
  readonly unique: boolean
}

interface FormValue {
  readonly importance: Importance
  readonly autoLicense: boolean
  readonly filterAppMostRecentSession: boolean
  readonly preventOnboardingFailingApplications: boolean
  readonly preventOnboardingFailingApplicationsBySession: boolean
  readonly policies: TypedPolicy[]
  readonly metadataFields: FormField[]
  readonly metadataRestrictNewApp: ReactionOperation
  readonly metadataRestrictExistingApp: ReactionOperation
  routeExpirationDays: number | null
  protectRuleEnv: string
}

export interface Settings {
  readonly importance: Importance
  readonly autoLicense: boolean
  readonly filterAppMostRecentSession: boolean
  readonly preventOnboardingFailingApplications: boolean
  readonly preventOnboardingFailingApplicationsBySession: boolean
  readonly compliancePolicies: Policy[]
  readonly remediationPolicies: Policy[]
  readonly customMetadata: {
    readonly fields: Field[]
    readonly settings: {
      readonly newAppAction: ReactionOperation
      readonly existingAppAction: ReactionOperation
    }
  }
  routeExpirationDays: number | null
  protectRuleEnv: string
}

interface SubmitSettings {
  readonly importance: Importance
  readonly autoLicense: boolean
  readonly filterAppMostRecentSession: boolean
  readonly preventOnboardingFailingApplications: boolean
  readonly preventOnboardingFailingApplicationsBySession: boolean
  readonly compliancePolicies: Policy[]
  readonly remediationPolicies: Policy[]
  readonly customMetadata: {
    readonly fields: SubmitField[]
    readonly settings: {
      readonly newAppAction: ReactionOperation
      readonly existingAppAction: ReactionOperation
    }
  }
  routeExpirationDays: number | null
  protectRuleEnv: string
}

export interface Props {
  readonly initialSettings?: Settings
  readonly availableCompliancePolicies: Policy[]
  readonly availableRemediationPolicies: Policy[]
  readonly availableRemediationNotifyPolicies: Policy[]
  readonly availableRemediationAutoRemediatePolicies: Policy[]
  readonly numberOfLicensesAvailable: number
  readonly numberOfLicensesUsed: number
  readonly onSubmit: (settings: SubmitSettings) => void
  readonly organizationID: string
  readonly currentUser?: any
}

interface State {
  readonly formValue: FormValue
  readonly formHasChanged: boolean
  readonly isDeleteCustomFieldDialogOpen: boolean
  readonly customFieldIndexToRemove?: number
}

const DEFAULT_ROUTE_EXPIRATION_DAYS = 30
const UNCHECKED_ROUTE_EXPIRATION_DAYS = 0
const ROUTE_EXPIRATION_DAYS_MAX = 365
const ROUTE_EXPIRATION_DAYS_MIN = 1

const HelpIcon = (props: any = {}) => (
  <div
    className={css`
      margin-left: 5px;
      margin-top: -3px;
    `}
  >
    <CsHelpCircledIcon
      color={theme.color.neutral50}
      height={16}
      width={16}
      {...props}
    />
  </div>
)

/* TODO Validation task pending, see CONTRAST-28675*/
class ApplicationsSettingsFormClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = getInitialState(props.initialSettings)
  }

  public render() {
    const isEmbeddedInNS = window.isEmbeddedInNS === true
    const {
      formValue: {
        autoLicense,
        filterAppMostRecentSession,
        preventOnboardingFailingApplications,
        preventOnboardingFailingApplicationsBySession,
        importance,
        policies,
        metadataFields,
        metadataRestrictNewApp,
        metadataRestrictExistingApp,
        routeExpirationDays,
        protectRuleEnv
      },
      formHasChanged,
      isDeleteCustomFieldDialogOpen = false,
      customFieldIndexToRemove
    } = this.state

    const {
      availableCompliancePolicies,
      availableRemediationNotifyPolicies,
      availableRemediationAutoRemediatePolicies,
      numberOfLicensesAvailable,
      numberOfLicensesUsed,
      onSubmit,
      organizationID,
      currentUser
    } = this.props

    const { isProtectEnabled } = currentUser
    const totalLicenses = numberOfLicensesAvailable + numberOfLicensesUsed
    const compliancePolicyFilterFunc = (p) => p.type === 'compliance'
    const remediationPolicyFilterFunc = (p) => p.type === 'remediation'
    const formFieldsMapFunc = (f) => {
      return {
        ...f,
        isTypeEditable: false
      }
    }
    let formFields: FormField[] = []
    const formSetStateBuilderFunc = ({ formValue }) => ({
      formValue: {
        ...formValue,
        metadataFields: formFields
      },
      formHasChanged: false
    })

    const isRestrictMissingFields =
      metadataRestrictExistingApp === 'DISABLE' ||
      metadataRestrictNewApp === 'DISABLE'
    const isRestrictOnNewApps =
      metadataRestrictExistingApp === 'NOOP' &&
      metadataRestrictNewApp === 'DISABLE'
    const isRestrictOnNewAndExistingApps =
      metadataRestrictExistingApp === 'DISABLE' &&
      metadataRestrictNewApp === 'DISABLE'

    const updateRestrictMissingFieldsState = (
      metadataRestrictNewApp: ReactionOperation,
      metadataRestrictExistingApp: ReactionOperation
    ) => {
      this.setState(({ formValue }) => ({
        formValue: {
          ...formValue,
          metadataRestrictNewApp,
          metadataRestrictExistingApp
        },
        formHasChanged: true
      }))
    }

    return (
      <I18nConsumer>
        {({ intl }) => (
          <FormValidator value={this.state.formValue}>
            {({ getFormFieldProps, formIsValid }) => (
              <form noValidate>
                <Row>
                  <Box basis={12}>
                    <FormattedMessage
                      id="detail_applicationSettings_configureDefaults"
                      children={(msg) => (
                        <div
                          className={cx(
                            readOnlyGridCss,
                            css`
                              margin-top: 3px;
                            `
                          )}
                          children={msg}
                        />
                      )}
                    />
                  </Box>
                </Row>
                <Row>
                  <Box basis={6}>
                    <FormattedMessage
                      id={intl.formatMessage({
                        id: 'title_onboardingManagement'
                      })}
                    >
                      {(msg) => {
                        return (
                          <div
                            className={cx(
                              formSectionTitleCss,
                              css`
                                margin-bottom: 10px;
                                color: ${theme.color.blue50};
                              `
                            )}
                          >
                            {msg}
                          </div>
                        )
                      }}
                    </FormattedMessage>
                    <FormattedMessage
                      id="title_appDefaults_defaultSettings"
                      children={(msg) => (
                        <div
                          className={cx(
                            formSectionTitleCss,
                            css`
                              margin-bottom: 10px;
                            `
                          )}
                          children={msg}
                        />
                      )}
                    />
                    <SingleSelectComboboxString<Importance>
                      id="organization_applications_settings_importance"
                      items={[
                        {
                          items: [
                            'CRITICAL',
                            'HIGH',
                            'MEDIUM',
                            'LOW',
                            'UNIMPORTANT'
                          ]
                        }
                      ]}
                      label={intl.formatMessage({
                        id: 'title_importance_level'
                      })}
                      itemToString={(importance) =>
                        intl.formatMessage({
                          id: importanceTo18N[importance]
                        })
                      }
                      onChange={this.formSetter('importance')}
                      value={importance}
                    />
                  </Box>
                </Row>
                <Row>
                  <Box basis={6}>
                    <MultiSelectCombobox<TypedPolicy>
                      label={intl.formatMessage({ id: 'title_policy' })}
                      noResultsText={intl.formatMessage({
                        id: 'detail_applicationSettings_noPoliciesEnabled'
                      })}
                      id="organization_applications_settings_policies"
                      items={[
                        {
                          name: 'Violation',
                          items: policiesWithType(
                            availableRemediationNotifyPolicies,
                            'remediation'
                          )
                        },
                        {
                          name: 'Auto-Verification',
                          items: policiesWithType(
                            availableRemediationAutoRemediatePolicies,
                            'remediation'
                          )
                        },
                        {
                          name: 'Compliance',
                          items: policiesWithType(
                            availableCompliancePolicies,
                            'compliance'
                          )
                        }
                      ]}
                      itemToString={(item) => item.name}
                      itemsEqual={(first, second) =>
                        first.type === second.type && first.id === second.id
                      }
                      itemToID={(item) => `${item.type}-${item.id}`}
                      onChange={this.formSetter('policies')}
                      values={policies}
                    />
                  </Box>
                </Row>
                <Row>
                  <Box basis={12}>
                    <div
                      className={css`
                        margin-top: 10px;
                      `}
                    >
                      <CustomMetadataForm
                        getFormFieldProps={getFormFieldProps}
                        fields={metadataFields}
                        onRemoveField={(indexToRemove) => {
                          this.setState(() => ({
                            isDeleteCustomFieldDialogOpen: true,
                            customFieldIndexToRemove: indexToRemove
                          }))
                        }}
                        onAddField={() => {
                          this.setState(({ formValue }) => ({
                            formValue: {
                              ...formValue,
                              metadataFields: formValue.metadataFields.concat([
                                {
                                  id: undefined,
                                  type: undefined,
                                  displayLabel: '',
                                  agentLabel: '',
                                  required: false,
                                  unique: false
                                }
                              ])
                            },
                            formHasChanged: true
                          }))
                        }}
                        onEdit={(inputField) => {
                          const {
                            type,
                            isTypeEditable,
                            displayLabel,
                            isRequired,
                            isUnique,
                            subfields
                          } = inputField

                          const updatedFields: FormField[] = metadataFields.map(
                            (f, i) => {
                              return i !== inputField.id
                                ? f
                                : {
                                    id: f.id,
                                    type,
                                    isTypeEditable,
                                    displayLabel,
                                    agentLabel: camelize(displayLabel),
                                    required: isRequired,
                                    unique: isUnique,
                                    subfields:
                                      !!subfields && subfields.length
                                        ? subfields.map((subfield) => {
                                            const {
                                              type,
                                              displayLabel,
                                              isRequired,
                                              isUnique
                                            } = subfield

                                            return {
                                              type,
                                              displayLabel,
                                              agentLabel:
                                                camelize(displayLabel),
                                              required: isRequired,
                                              unique: isUnique
                                            }
                                          })
                                        : undefined
                                  }
                            }
                          )

                          this.setState(({ formValue }) => ({
                            formValue: {
                              ...formValue,
                              metadataFields: updatedFields
                            },
                            formHasChanged: true
                          }))
                        }}
                      />
                    </div>
                  </Box>
                </Row>
                <Row>
                  <Box basis={9}>
                    <div
                      className={css`
                        display: inline-flex;
                        margin-bottom: 4px;
                      `}
                    >
                      <FormattedMessage
                        id={intl.formatMessage({
                          id: 'title_onboardingRestrictions'
                        })}
                      >
                        {(msg) => (
                          <div className={cx(formSectionTitleCss)}>{msg}</div>
                        )}
                      </FormattedMessage>
                    </div>
                    <Checkbox
                      data-testid="prevent_onboarding_failing_applications"
                      id="prevent_onboarding_failing_applications"
                      label=""
                      renderLabelContent={() => (
                        <Text font="body">
                          {intl.formatMessage(
                            {
                              id: 'detail_preventOnboardingFailingApplications_checkbox'
                            },
                            {
                              ...formatStyles.bold
                            }
                          )}
                        </Text>
                      )}
                      value={preventOnboardingFailingApplications}
                      onChange={this.formSetter(
                        'preventOnboardingFailingApplications'
                      )}
                    />
                    <Checkbox
                      data-testid="prevent_onboarding_failing_applications_by_session"
                      id="prevent_onboarding_failing_applications_by_session"
                      label=""
                      renderLabelContent={() => (
                        <Text font="body">
                          {intl.formatMessage(
                            {
                              id: 'detail_preventOnboardingFailingApplicationsBySession_checkbox'
                            },
                            {
                              ...formatStyles.bold
                            }
                          )}
                        </Text>
                      )}
                      value={preventOnboardingFailingApplicationsBySession}
                      onChange={this.formSetter(
                        'preventOnboardingFailingApplicationsBySession'
                      )}
                    />
                    <div>
                      <FormattedMessage id="detail_customMetadata_restrictAppsOnMissingFields">
                        {(label) => {
                          return (
                            <Checkbox
                              renderHelpContent={() => (
                                <div>
                                  <FormattedMessage id="title_customMetadata_restrictApplications">
                                    {(msg) => (
                                      <p
                                        className={css`
                                          font: ${theme.font.label.font};,
                                        `}
                                      >
                                        {msg}
                                      </p>
                                    )}
                                  </FormattedMessage>
                                  <FormattedMessage id="detail_customMetadata_restrictApps">
                                    {(msg) => <HelpText>{msg}</HelpText>}
                                  </FormattedMessage>
                                  <p className={metadataCss}>
                                    <FormattedMessage
                                      id={
                                        'detail_customMetadata_restrictAppsSupport'
                                      }
                                      values={{
                                        a: (...msg) => (
                                          <a
                                            href={intl.formatMessage({
                                              id: 'link_organizationApplications'
                                            })}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {msg}
                                          </a>
                                        )
                                      }}
                                    />
                                  </p>
                                </div>
                              )}
                              id="restrict_app_on_missing_fields"
                              data-testid="restrict_app_on_missing_fields"
                              label=""
                              renderLabelContent={() => (
                                <Text font="body">{label}</Text>
                              )}
                              value={isRestrictMissingFields}
                              onChange={(isChecked) => {
                                if (isChecked) {
                                  updateRestrictMissingFieldsState(
                                    'DISABLE',
                                    'NOOP'
                                  )
                                } else {
                                  updateRestrictMissingFieldsState(
                                    'NOOP',
                                    'NOOP'
                                  )
                                }
                              }}
                            />
                          )
                        }}
                      </FormattedMessage>
                    </div>

                    {isRestrictMissingFields && (
                      <div
                        className={css`
                          display: flex;
                          flex-direction: column;
                          margin-left: 26px;
                          margin-top: 15px;
                        `}
                      >
                        <Radio
                          label={intl.formatMessage({
                            id: 'detail_customMetadata_restrictNewApps'
                          })}
                          testId="restricted-new-apps"
                          id="restricted-new-apps"
                          onChange={(isSelected) => {
                            if (isSelected) {
                              updateRestrictMissingFieldsState(
                                'DISABLE',
                                'NOOP'
                              )
                            }
                          }}
                          isChecked={isRestrictOnNewApps}
                        />

                        <Radio
                          label={intl.formatMessage({
                            id: 'detail_customMetadata_restrictNewAndExistingApps'
                          })}
                          testId="restricted-new-and-existing-apps"
                          id="restricted-new-and-existing-apps"
                          onChange={(isSelected) => {
                            if (isSelected) {
                              updateRestrictMissingFieldsState(
                                'DISABLE',
                                'DISABLE'
                              )
                            }
                          }}
                          isChecked={isRestrictOnNewAndExistingApps}
                        />
                      </div>
                    )}
                  </Box>
                </Row>
                <Row>
                  <Box basis={6}>
                    <div
                      className={css`
                        margin-bottom: 4px;
                      `}
                    >
                      <FormattedMessage
                        id={intl.formatMessage({ id: 'title_licenses' })}
                      >
                        {(msg) => (
                          <span className={cx(formSectionTitleCss)}>{msg}</span>
                        )}
                      </FormattedMessage>
                    </div>
                    <div
                      className={css`
                        margin-bottom: 4px;
                      `}
                    >
                      <span
                        className={css`
                          font-weight: 700;
                          font-size: 17px;
                        `}
                      >
                        {numberOfLicensesUsed}
                      </span>{' '}
                      /{' '}
                      {isEmbeddedInNS ? (
                        <>{totalLicenses}</>
                      ) : (
                        <Link
                          href={`#/${organizationID}/reports/stats/licenses`}
                        >
                          {totalLicenses}
                        </Link>
                      )}{' '}
                      {intl.formatMessage({ id: 'detail_licensesUsed' })}
                    </div>
                    <ProgressThermometer
                      renderProgress={({ width, className }) => {
                        return <div className={className} style={{ width }} />
                      }}
                      bgColor="neutral40"
                      fillColor="neutral80"
                      progress={numberOfLicensesUsed}
                      maxProgress={totalLicenses}
                    />
                  </Box>
                </Row>
                <Row>
                  <Box basis={6}>
                    <div
                      className={css`
                        margin-bottom: 4px;
                      `}
                    >
                      <FormattedMessage
                        id={intl.formatMessage({ id: 'title_behavior' })}
                      >
                        {(msg) => (
                          <div className={cx(formSectionTitleCss)}>{msg}</div>
                        )}
                      </FormattedMessage>
                    </div>
                    <Checkbox
                      id="auto_apply_license_to_applications"
                      label={intl.formatMessage({
                        id: 'detail_automaticallyApplyLicensesToApplications_checkbox'
                      })}
                      value={autoLicense}
                      onChange={this.formSetter('autoLicense')}
                    />
                  </Box>
                </Row>
                <Row>
                  <Box basis={12}>
                    <div>
                      <FormattedMessage
                        id={intl.formatMessage({
                          id: 'title_application_dataManagement'
                        })}
                      >
                        {(msg) => (
                          <div
                            className={cx(
                              formSectionTitleCss,
                              css`
                                margin-bottom: 10px;
                                color: ${theme.color.blue50};
                              `
                            )}
                          >
                            {msg}
                          </div>
                        )}
                      </FormattedMessage>
                    </div>
                    <div
                      className={css`
                        margin-bottom: 4px;
                      `}
                    >
                      <Label
                        className={css`
                          color: ${theme.color.black};
                        `}
                      >
                        {intl.formatMessage({
                          id: 'title_route_expiration_policy'
                        })}
                      </Label>
                    </div>
                    <div
                      className={cx(
                        readOnlyGridCss,
                        css`
                          margin-top: 3px;
                          margin-bottom: 15px;
                        `
                      )}
                    >
                      {intl.formatMessage(
                        {
                          id: 'detail_applicationSettings_routeExpirationPolicy'
                        },
                        {
                          ...formatStyles.doclink({
                            path: 'configure-route-expiration.html'
                          })
                        }
                      )}
                    </div>
                    <div
                      className={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                      `}
                    >
                      <Checkbox
                        id="enable_route_expiration_policy"
                        data-testid="enable_route_expiration_policy"
                        label={intl.formatMessage({
                          id: 'detail_enableRouteExpirationPolicy_checkbox'
                        })}
                        value={
                          routeExpirationDays === null ||
                          Number(routeExpirationDays) > 0
                        }
                        onChange={() => {
                          if (
                            routeExpirationDays === null ||
                            routeExpirationDays > 0
                          ) {
                            return this.formSetter('routeExpirationDays')(
                              UNCHECKED_ROUTE_EXPIRATION_DAYS
                            )
                          }
                          return this.formSetter('routeExpirationDays')(
                            DEFAULT_ROUTE_EXPIRATION_DAYS
                          )
                        }}
                      />
                      <div
                        className={css`
                          input {
                            width: 45px;
                          }
                          div {
                            flex-direction: row;
                            align-items: center;
                            padding-left: 3px;
                            margin-top: -7px;
                          }
                        `}
                      >
                        <NumberInput
                          data-testid="route_expiration_number_days"
                          label={intl.formatMessage({
                            id: 'detail_enableRouteExpirationPolicy_numberInput'
                          })}
                          max={`${ROUTE_EXPIRATION_DAYS_MAX}`}
                          min={`${ROUTE_EXPIRATION_DAYS_MIN}`}
                          onChange={(_evt, value: string | undefined) => {
                            if (Number(value) > ROUTE_EXPIRATION_DAYS_MAX) {
                              this.formSetter('routeExpirationDays')(
                                Number(ROUTE_EXPIRATION_DAYS_MAX)
                              )
                            } else if (
                              Number(value) < ROUTE_EXPIRATION_DAYS_MIN
                            ) {
                              this.formSetter('routeExpirationDays')(
                                Number(ROUTE_EXPIRATION_DAYS_MIN)
                              )
                            } else {
                              this.formSetter('routeExpirationDays')(
                                Number(value) || DEFAULT_ROUTE_EXPIRATION_DAYS
                              )
                            }
                          }}
                          step="1"
                          value={
                            routeExpirationDays === null ||
                            Number(routeExpirationDays) <=
                              UNCHECKED_ROUTE_EXPIRATION_DAYS
                              ? `${DEFAULT_ROUTE_EXPIRATION_DAYS}`
                              : `${routeExpirationDays}`
                          }
                          isDisabled={
                            routeExpirationDays === null ||
                            Number(routeExpirationDays) <=
                              UNCHECKED_ROUTE_EXPIRATION_DAYS
                          }
                        />
                      </div>
                      <div
                        className={css`
                          padding-left: 5px;
                        `}
                      >
                        <FormattedMessage id="detail_enableRouteExpirationPolicy_checkboxPart2" />
                      </div>
                    </div>
                  </Box>
                </Row>

                <SplitContext.Consumer>
                  {() => {
                    return (
                      isProtectEnabled && (
                        <Row>
                          <Box basis={12}>
                            <FormattedMessage
                              id={intl.formatMessage({
                                id: 'title_triageManagement'
                              })}
                            >
                              {(msg) => {
                                return (
                                  <span
                                    className={cx(
                                      formSectionTitleCss,
                                      css`
                                        color: ${theme.color.blue50};
                                      `
                                    )}
                                  >
                                    {msg}
                                  </span>
                                )
                              }}
                            </FormattedMessage>
                          </Box>
                          <Box basis={12}>
                            <div
                              className={css`
                                margin-bottom: 4px;
                                display: flex;
                              `}
                            >
                              <Label
                                className={css`
                                  color: ${theme.color.black};
                                `}
                              >
                                {intl.formatMessage({
                                  id: 'title_correlateAdrToAssessVulnerabilities'
                                })}
                              </Label>
                            </div>
                            <div
                              // prettier-ignore
                              className={css`
                                display: flex;
                                flex-direction: row;
                                align-items: center;
                                gap: 8px;

                                /* temp: hide clear button on select, Dropdown does not call itemToString */
                                & button[data-testid='select-protect-assess-env-actions-start-adornment'] {
                                  display: none !important;
                                }
                              `}
                            >
                              {intl.formatMessage(
                                { id: 'detail_selectProtectEnvironment' },
                                {
                                  select: () => (
                                    <SingleSelect
                                      testId="select-protect-assess-env"
                                      style={{ marginTop: '-8px' }}
                                      label={intl.formatMessage({
                                        id: 'title_protectEnvironment'
                                      })}
                                      itemToID={(it) => it}
                                      itemToString={(it) =>
                                        intl.formatMessage({
                                          id: serverEnvI18n[it]
                                        })
                                      }
                                      items={allServerEnvironments}
                                      value={protectRuleEnv}
                                      onChange={(_evt, value: string) => {
                                        this.formSetter('protectRuleEnv')(value)
                                      }}
                                    />
                                  )
                                }
                              )}
                            </div>
                          </Box>
                        </Row>
                      )
                    )
                  }}
                </SplitContext.Consumer>
                <Row>
                  <Box basis={6}>
                    <div
                      className={css`
                        display: inline-flex;
                        margin-bottom: 4px;
                      `}
                    >
                      <FormattedMessage
                        id={intl.formatMessage({
                          id: 'title_sessionMetadata'
                        })}
                      >
                        {(msg) => (
                          <div className={cx(formSectionTitleCss)}>{msg}</div>
                        )}
                      </FormattedMessage>
                      <WithTooltip
                        renderTooltipContent={() => (
                          <div>
                            <HelpText>
                              <FormattedMessage
                                id={
                                  'detail_sessionMetada_mostRecentFilterCheckbox'
                                }
                              />
                            </HelpText>
                          </div>
                        )}
                        placement="top"
                      >
                        {({ onMouseEnter, onMouseLeave }) => (
                          <HelpIcon
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                          />
                        )}
                      </WithTooltip>
                    </div>
                    <div
                      className={css`
                        margin-bottom: 10px;
                      `}
                    >
                      <Checkbox
                        id="auto_apply_filter_app_details_most_recent_session"
                        label={intl.formatMessage({
                          id: 'detail_filterAppDetailsByMostRecentSession_checkbox'
                        })}
                        value={filterAppMostRecentSession}
                        onChange={this.formSetter('filterAppMostRecentSession')}
                      />
                    </div>
                  </Box>
                </Row>
                <Row>
                  <ConfirmationDialog
                    size="small"
                    isOpen={isDeleteCustomFieldDialogOpen}
                    onCancel={() => {
                      this.setState({
                        isDeleteCustomFieldDialogOpen: false,
                        customFieldIndexToRemove: undefined
                      })
                    }}
                    onConfirm={() => {
                      const updatedMetadataFields = metadataFields.filter(
                        (_, index) => index !== customFieldIndexToRemove
                      )

                      this.setState(({ formValue }) => ({
                        formValue: {
                          ...formValue,
                          metadataFields:
                            updatedMetadataFields.length > 0
                              ? updatedMetadataFields
                              : [getEmptyFieldInput()]
                        },
                        formHasChanged: true,
                        isDeleteCustomFieldDialogOpen: false,
                        customFieldIndexToRemove: undefined
                      }))
                    }}
                    renderConfirmMessage={() => (
                      <FormattedMessage id="action_delete" />
                    )}
                    renderCancelMessage={() => (
                      <FormattedMessage id="action_cancel" />
                    )}
                    renderTitle={() => (
                      <FormattedMessage id="title_customMetadata_removeField" />
                    )}
                    renderBody={() => {
                      const metadataToRemove = metadataFields.find(
                        (_, index) => index === customFieldIndexToRemove
                      )

                      return (
                        !!metadataToRemove && (
                          <FormattedMessage
                            id="detail_customMetadata_removeCustomMetadataField"
                            values={{
                              fieldName: <b>{metadataToRemove.displayLabel}</b>
                            }}
                          />
                        )
                      )
                    }}
                  />
                </Row>

                <Row>
                  <Box basis={8}>
                    <div>
                      <FormattedMessage
                        id={'title_vulnerabilityManagement'}
                        children={(vulnManagementLink) => (
                          <FormattedMessage
                            id={
                              'variable_applicationSettings_lookingForApprovalWorkflow'
                            }
                            values={{
                              vulnManagementLink: isEmbeddedInNS ? (
                                vulnManagementLink
                              ) : (
                                <a
                                  className={css`
                                    cursor: pointer;
                                  `}
                                  onClick={() =>
                                    updateRoute(
                                      `/${organizationID}/rules/vulnerability-management`
                                    )
                                  }
                                >
                                  {vulnManagementLink}
                                </a>
                              )
                            }}
                          />
                        )}
                      />
                    </div>
                  </Box>
                  <Box basis={4}>
                    <div
                      className={css`
                        display: flex;
                        flex-direction: row-reverse;
                      `}
                    >
                      <Button
                        isDisabled={!formIsValid || !formHasChanged}
                        onClick={(e) => {
                          e.preventDefault()
                          try {
                            const filteredFields =
                              getFilteredFields(metadataFields)
                            onSubmit({
                              autoLicense,
                              filterAppMostRecentSession,
                              preventOnboardingFailingApplications,
                              preventOnboardingFailingApplicationsBySession,
                              routeExpirationDays,
                              protectRuleEnv,
                              importance,
                              compliancePolicies: policies.filter(
                                compliancePolicyFilterFunc
                              ),
                              remediationPolicies: policies.filter(
                                remediationPolicyFilterFunc
                              ),
                              customMetadata: {
                                fields: filteredFields,
                                settings: {
                                  newAppAction: metadataRestrictNewApp,
                                  existingAppAction: metadataRestrictExistingApp
                                }
                              }
                            })

                            formFields = filteredFields.map(formFieldsMapFunc)

                            if (!!formFields && formFields.length === 0) {
                              formFields.push(getEmptyFieldInput())
                            }

                            this.setState(formSetStateBuilderFunc)

                            logSuccess(
                              intl.formatMessage({
                                id: 'detail_applicationSettings_successSaveMsg'
                              })
                            )
                          } catch (_) {
                            logError(
                              intl.formatMessage({
                                id: 'detail_applicationSettings_errorSaveMsg'
                              })
                            )
                          }
                        }}
                      >
                        {intl.formatMessage({
                          id: 'action_save'
                        })}
                      </Button>
                    </div>
                  </Box>
                </Row>
              </form>
            )}
          </FormValidator>
        )}
      </I18nConsumer>
    )
  }

  public componentDidUpdate(prevProps: Props) {
    if (!prevProps.initialSettings && this.props.initialSettings) {
      this.setState(getInitialState(this.props.initialSettings))
    }
  }

  private formSetter = <K extends keyof FormValue>(
    key: K
  ): ((v: FormValue[K]) => void) => {
    return (value) =>
      this.setState(({ formValue }) => ({
        formValue: {
          ...formValue,
          [key]: value
        },
        formHasChanged: true
      }))
  }
}

function getInitialState(initialValues?: Props['initialSettings']): State {
  if (!initialValues) {
    return {
      formValue: {
        importance: 'MEDIUM',
        autoLicense: false,
        filterAppMostRecentSession: false,
        preventOnboardingFailingApplications: false,
        preventOnboardingFailingApplicationsBySession: false,
        policies: [],
        metadataFields: [],
        metadataRestrictExistingApp: 'NOOP',
        metadataRestrictNewApp: 'NOOP',
        routeExpirationDays: UNCHECKED_ROUTE_EXPIRATION_DAYS,
        protectRuleEnv: 'PRODUCTION'
      },
      formHasChanged: false,
      isDeleteCustomFieldDialogOpen: false,
      customFieldIndexToRemove: undefined
    }
  }

  const {
    compliancePolicies,
    remediationPolicies,
    autoLicense,
    filterAppMostRecentSession,
    preventOnboardingFailingApplications,
    preventOnboardingFailingApplicationsBySession,
    importance,
    customMetadata: {
      fields,
      settings: { newAppAction, existingAppAction }
    },
    routeExpirationDays,
    protectRuleEnv
  } = initialValues

  return {
    formValue: {
      policies: policiesWithType(compliancePolicies, 'compliance').concat(
        policiesWithType(remediationPolicies, 'remediation')
      ),
      autoLicense,
      filterAppMostRecentSession,
      preventOnboardingFailingApplications,
      preventOnboardingFailingApplicationsBySession,
      importance,
      metadataFields:
        !!fields && fields.length
          ? fields.map((field) => {
              return {
                ...field,
                isTypeEditable: false,
                subfields:
                  !!field.subfields && field.subfields.length
                    ? field.subfields.map((subfield) => {
                        return {
                          ...subfield
                        }
                      })
                    : undefined
              }
            })
          : [getEmptyFieldInput()],
      metadataRestrictNewApp: newAppAction,
      metadataRestrictExistingApp: existingAppAction,
      routeExpirationDays,
      protectRuleEnv
    },
    formHasChanged: false,
    isDeleteCustomFieldDialogOpen: false,
    customFieldIndexToRemove: undefined
  }
}

function policiesWithType(policies: Policy[], type: PolicyType): TypedPolicy[] {
  return policies.map(({ name, id }) => ({ name, id, type }))
}

export function camelize(str: string) {
  return !!str && str.length
    ? str.charAt(0).toLowerCase() +
        str
          .slice(1)
          .trim()
          .replace(/[-_\s]+(.)?/g, (_, chr) => {
            return !!chr ? chr && chr.toUpperCase() : ''
          })
    : ''
}

export const getFilteredFields = (fields: FormField[]) => {
  const fieldsWithType: SubmitField[] = []

  fields.forEach((field) => {
    const { id, type, displayLabel, agentLabel, required, unique, subfields } =
      field

    if (!!type) {
      const subfieldsWithType: SubmitSubfield[] = []

      if (!!subfields && subfields.length) {
        subfields.forEach((subfield) => {
          const {
            type: subfieldType,
            agentLabel: theAgentLabel,
            displayLabel: theDisplayLabel,
            id: theId,
            required: theRequired,
            unique: theUnique
          } = subfield
          if (!!subfieldType) {
            subfieldsWithType.push({
              agentLabel: theAgentLabel,
              displayLabel: theDisplayLabel,
              id: theId,
              required: theRequired,
              unique: theUnique,
              type: subfieldType
            })
          }
        })
      }

      fieldsWithType.push({
        id,
        type,
        displayLabel,
        agentLabel,
        required,
        unique,
        subfields: subfieldsWithType
      })
    }
  })

  return fieldsWithType
    .filter((f) => !!f.type && f.displayLabel !== '')
    .filter(
      (f) =>
        f.type !== 'PERSON_OF_CONTACT' || (!!f.subfields && f.subfields.length)
    )
}

const getEmptyFieldInput = (): FormField => {
  return {
    id: undefined,
    type: undefined,
    displayLabel: '',
    agentLabel: '',
    required: false,
    unique: false,
    isTypeEditable: true
  }
}

// inject current user into props since above
// is a class component and we cannot user hooks within it
const withCurrentUser = (Component) => (props) => {
  const { currentUser = {} } = useCurrentUser()

  return <Component currentUser={currentUser} {...props} />
}

const ApplicationsSettingsForm = withCurrentUser(ApplicationsSettingsFormClass)

export { ApplicationsSettingsForm }
